'use client';
import gsap from 'gsap';
import {
  forwardRef,
  memo,
  ReactNode,
  useImperativeHandle,
  useRef,
} from 'react';
import {
  BackSide,
  Color,
  IUniform,
  Mesh,
  ShaderMaterial,
  Uniform,
  Vector3,
} from 'three';

import promiseTimeline from 'utils/promiseTimeline';

import fragmentShader from './shaders/fragment.glsl';
import vertexShader from './shaders/vertex.glsl';

const SCALE = new Vector3(1.02, 1.02, 1.02);

export interface AtmosphereRef {
  playOutro: () => Promise<any>;
  playWatchAgain: () => Promise<any>;
  sunDirection: IUniform<Vector3>;
}

interface AtmosphereProps {
  geometry: ReactNode;
  sunDirection: Vector3;

  dayColor: Color;
  twilightColor: Color;
}

const Atmosphere = forwardRef<AtmosphereRef, AtmosphereProps>(
  ({ geometry, sunDirection, dayColor, twilightColor }, ref) => {
    const materialRef = useRef<ShaderMaterial>(
      new ShaderMaterial({
        vertexShader,
        fragmentShader,
        side: BackSide,
        transparent: true,
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -1,
        uniforms: {
          uSunDirection: new Uniform(sunDirection),
          uAtmosphereDayColor: new Uniform(dayColor),
          uAtmosphereTwilightColor: new Uniform(twilightColor),
          uNightFactor: { value: 1.0 },
          uOutroFactor: { value: 0.0 },
        },
      })
    );
    const atmosphereRef = useRef<Mesh>(null);

    useImperativeHandle(ref, () => ({
      sunDirection: materialRef.current.uniforms.uSunDirection,
      playOutro: () => {
        return new Promise(resolve => {
          gsap
            .timeline()
            .to(materialRef.current.uniforms.uOutroFactor, {
              value: 1.0,
              duration: 0.5,
            })
            .to(atmosphereRef.current!.scale, {
              x: 1.52,
              y: 1.52,
              z: 1.52,
              duration: 2,
              onComplete: () => resolve(true),
            });
        });
      },
      playWatchAgain: () => {
        return promiseTimeline(materialRef.current.uniforms.uOutroFactor, {
          value: 0,
          duration: 2,
        });
      },
    }));

    return (
      <mesh
        name="atmosphere"
        scale={SCALE}
        frustumCulled={false}
        renderOrder={2}
        ref={atmosphereRef}
      >
        {geometry}
        <primitive object={materialRef.current} attach="material" />
      </mesh>
    );
  }
);

Atmosphere.displayName = 'Atmosphere';
export default memo(Atmosphere);
